import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, ChevronRight } from "lucide-react";
import ApplicantShell from "../components/ApplicantShell";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import B1SpeakingAudioRecorder from "../components/language/B1SpeakingAudioRecorder";
import ApplicantDailyBriefing from "../components/applicant/ApplicantDailyBriefing";
import { useApplicantCoach } from "../components/applicant/ApplicantCoachContext";
import {
  buildApplicantDashboardCoachSurface,
  buildApplicantDailyBriefing,
} from "../components/applicant/applicantDailyBriefingModel";
import config from "../config/config";
import { buildAuthFetchOptions, clearStoredAuth } from "../utils/authSession";

async function fetchJson(path, options = {}) {
  const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData;
  const response = await fetch(
    `${config.API_BASE_URL}${path}`,
    buildAuthFetchOptions({
      ...options,
      headers: {
        ...(!isFormData && options.body ? { "Content-Type": "application/json" } : {}),
        ...(options.headers || {}),
      },
    })
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data?.message || data?.detail || `Request failed (${response.status})`);
    error.status = response.status;
    error.detail = data?.detail || "";
    throw error;
  }
  return data;
}

function extractModules(attempt) {
  return attempt?.answers?.modules || {};
}

function buildDraftState(attempt) {
  const saved = attempt?.answers?.draft_responses || {};
  return {
    reading: saved.reading || {},
    listening: saved.listening || {},
    language_elements: saved.language_elements || {},
    writing_answer: saved.writing_answer || "",
    speaking_audio_meta: saved.speaking_audio_meta || null,
    speaking_status: attempt?.speaking_status || saved.speaking_status || "not_started",
    speaking_invalid_reason: attempt?.speaking_invalid_reason || saved.speaking_invalid_reason || "",
    modules: extractModules(attempt),
  };
}

function buildAttemptPayload(draftState) {
  return {
    reading_answers: Object.entries(draftState.reading || {}).map(([item_id, selected_option]) => ({ item_id, selected_option })),
    listening_answers: Object.entries(draftState.listening || {}).map(([item_id, selected_option]) => ({ item_id, selected_option })),
    language_elements_answers: Object.entries(draftState.language_elements || {}).map(([item_id, selected_option]) => ({ item_id, selected_option })),
    writing_answer: draftState.writing_answer || "",
  };
}

function formatDate(value) {
  if (!value) return "Noch offen";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });
}

function formatAbsoluteDate(value) {
  if (!value) return "Noch offen";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function formatScore(value) {
  return value === null || value === undefined ? "Noch offen" : `${Math.round(Number(value))}`;
}

function formatAttemptType(value) {
  if (value === "baseline") return "Baseline";
  if (value === "follow_up") return "Folgetest";
  return "Noch offen";
}

function formatReviewStatus(value) {
  if (value === "reviewed") return "Geprüft";
  if (value === "pending") return "In Prüfung";
  if (value === "not_required") return "Keine manuelle Prüfung nötig";
  return value || "Noch offen";
}

function formatReadinessStateCopy(value) {
  if (value === "ready") return "Ready";
  if (value === "in_progress") return "In progress";
  if (value === "blocked") return "Blocked";
  if (value === "missing") return "Missing";
  if (value === "not_required_now") return "Not required now";
  return "In progress";
}

function renderChoiceModule(items, fieldName, values, onChange) {
  return (items || []).map((item, index) => (
    <div key={item.id} className="dashboard-question-card">
      <p className="dashboard-question-card__title">{index + 1}. {item.prompt}</p>
      {item.transcript ? <p className="dashboard-question-card__copy">{item.transcript}</p> : null}
      <div className="dashboard-question-options">
        {item.options?.map((option, optionIndex) => (
          <label key={`${item.id}-${optionIndex}`} className="dashboard-option-row">
            <input
              type="radio"
              name={`${fieldName}-${item.id}`}
              checked={values?.[item.id] === optionIndex}
              onChange={() => onChange(fieldName, item.id, optionIndex)}
            />
            <span>{option}</span>
          </label>
        ))}
      </div>
    </div>
  ));
}

export default function ApplicantDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { registerCoachSurface } = useApplicantCoach();
  const [searchParams, setSearchParams] = useSearchParams();
  const [snapshot, setSnapshot] = useState(null);
  const [snapshotLoading, setSnapshotLoading] = useState(true);
  const [snapshotError, setSnapshotError] = useState("");
  const [messageWorkspace, setMessageWorkspace] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [activeAttempt, setActiveAttempt] = useState(null);
  const [draftState, setDraftState] = useState(null);
  const [languageLoading, setLanguageLoading] = useState(true);
  const [languageError, setLanguageError] = useState("");
  const [languageNotice, setLanguageNotice] = useState("");
  const [actionBusy, setActionBusy] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draftDirty, setDraftDirty] = useState(false);
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState("Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.");

  useEffect(() => {
    document.title = "Experts4Europe | Applicant Readiness OS";
  }, []);

  useEffect(() => {
    if (location.hash === "#sprachtest-b1") {
      navigate("/dashboard/applicant/sprachtest-b1", { replace: true });
      return;
    }
    const legacyPanel = searchParams.get("panel");
    if (legacyPanel === "language") {
      navigate("/dashboard/applicant/sprachtest-b1", { replace: true });
      return;
    }
    if (legacyPanel === "matches") {
      navigate("/dashboard/applicant/matches", { replace: true });
      return;
    }
    if (legacyPanel === "messages") {
      navigate("/dashboard/applicant/nachrichten", { replace: true });
    }
  }, [location.hash, navigate, searchParams]);

  async function loadMissionControl() {
    setSnapshotLoading(true);
    setSnapshotError("");
    try {
      setSnapshot(await fetchJson("/api/applicant-dashboard/mission-control"));
    } catch (err) {
      if (handleAuthError(err)) return;
      setSnapshotError(err.message || "Die Mission-Control-Sicht konnte nicht geladen werden.");
    } finally {
      setSnapshotLoading(false);
    }
  }

  async function loadLanguageTestState() {
    setLanguageLoading(true);
    setLanguageError("");
    try {
      const [attemptsData, latestData] = await Promise.all([
        fetchJson("/api/language-tests/b1/me/attempts"),
        fetchJson("/api/language-tests/b1/me/latest"),
      ]);
      const rows = Array.isArray(attemptsData?.rows) ? attemptsData.rows : [];
      const draftAttempt = latestData?.draft_attempt || rows.find((row) => row.status === "draft") || null;
      setAttempts(rows);
      setActiveAttempt(draftAttempt);
      setDraftState(draftAttempt ? buildDraftState(draftAttempt) : null);
      setDraftDirty(false);
    } catch (err) {
      if (handleAuthError(err)) return;
      setLanguageError(err.message || "Der Sprachtest konnte nicht geladen werden.");
    } finally {
      setLanguageLoading(false);
    }
  }

  async function loadMessageWorkspace() {
    try {
      setMessageWorkspace(await fetchJson("/api/applicant/messages/workspace"));
    } catch (err) {
      if (handleAuthError(err)) return;
      setMessageWorkspace(null);
    }
  }

  async function loadAll() {
    await Promise.all([loadMissionControl(), loadLanguageTestState(), loadMessageWorkspace()]);
  }

  useEffect(() => {
    loadAll();
  }, []);

  function handleAuthError(err) {
    if (Number(err?.status) !== 401) return false;

    clearStoredAuth();
    setDialogOpen(false);
    setActionBusy(false);
    setSnapshotError("");
    setLanguageError("");
    setSnapshotLoading(false);
    setLanguageLoading(false);

    const detail = String(err?.detail || err?.message || "").toLowerCase();
    const isTimeout = detail.includes("expired") || detail.includes("inactivity") || detail.includes("session");

    setSessionExpiredMessage(
      isTimeout
        ? "Du wurdest wegen Inaktivität automatisch abgemeldet. Bitte melde dich erneut an."
        : "Deine Anmeldung ist nicht mehr gültig. Bitte melde dich erneut an."
    );
    setSessionExpiredOpen(true);
    return true;
  }

  function setPanel(panel) {
    const next = new URLSearchParams(searchParams);
    if (panel === "overview") next.delete("panel");
    else next.set("panel", panel);
    setSearchParams(next, { replace: true });
  }

  async function startAttempt(testType) {
    setActionBusy(true);
    setLanguageError("");
    setLanguageNotice("");
    try {
      const result = await fetchJson("/api/language-tests/b1/attempts", { method: "POST", body: JSON.stringify({ test_type: testType }) });
      setActiveAttempt(result.attempt);
      setDraftState(buildDraftState(result.attempt));
      setDraftDirty(false);
      setDialogOpen(true);
      setPanel("language");
      setLanguageNotice(testType === "baseline" ? "Baseline gestartet." : "Folgetest gestartet.");
      await loadAll();
    } catch (err) {
      if (handleAuthError(err)) return;
      setLanguageError(err.message || "Der Test konnte nicht gestartet werden.");
    } finally {
      setActionBusy(false);
    }
  }

  async function saveDraft() {
    if (!activeAttempt || !draftState) return;
    setActionBusy(true);
    setLanguageError("");
    setLanguageNotice("");
    try {
      const result = await fetchJson(`/api/language-tests/b1/attempts/${activeAttempt.id}/draft`, { method: "PATCH", body: JSON.stringify(buildAttemptPayload(draftState)) });
      setActiveAttempt(result.attempt);
      setDraftState(buildDraftState(result.attempt));
      setDraftDirty(false);
      setLanguageNotice("Entwurf gespeichert.");
      await loadAll();
    } catch (err) {
      if (handleAuthError(err)) return;
      setLanguageError(err.message || "Der Entwurf konnte nicht gespeichert werden.");
    } finally {
      setActionBusy(false);
    }
  }

  async function submitAttempt() {
    if (!activeAttempt || !draftState) return;
    setActionBusy(true);
    setLanguageError("");
    setLanguageNotice("");
    try {
      await fetchJson(`/api/language-tests/b1/attempts/${activeAttempt.id}/submit`, { method: "POST", body: JSON.stringify(buildAttemptPayload(draftState)) });
      setDialogOpen(false);
      setActiveAttempt(null);
      setDraftState(null);
      setDraftDirty(false);
      setLanguageNotice("Der B1-Core wurde eingereicht.");
      await loadAll();
    } catch (err) {
      if (handleAuthError(err)) return;
      setLanguageError(err.message || "Der Test konnte nicht eingereicht werden.");
    } finally {
      setActionBusy(false);
    }
  }

  async function uploadSpeakingAudio(file, durationMs) {
    if (!activeAttempt?.id) return;
    setActionBusy(true);
    setLanguageError("");
    setLanguageNotice("");
    try {
      const formData = new FormData();
      formData.append("audio", file);
      formData.append("duration_ms", String(durationMs || 0));
      const result = await fetchJson(`/api/language-tests/b1/attempts/${activeAttempt.id}/speaking-audio`, { method: "POST", body: formData });
      setActiveAttempt(result.attempt);
      setDraftState(buildDraftState(result.attempt));
      setDraftDirty(false);
      setLanguageNotice("Sprachaufnahme gespeichert.");
      await loadAll();
    } catch (err) {
      if (handleAuthError(err)) return;
      setLanguageError(err.message || "Die Sprachaufnahme konnte nicht gespeichert werden.");
      throw err;
    } finally {
      setActionBusy(false);
    }
  }

  const readiness = snapshot?.readiness || null;
  const notifications = snapshot?.notifications || [];
  const messageHighlights = messageWorkspace?.highlights || [];
  const opportunities = snapshot?.opportunities || [];
  const primaryAction = snapshot?.actions?.primary;
  const secondaryActions = snapshot?.actions?.secondary || [];
  const currentModules = draftState?.modules || {};
  const readinessOperating = readiness?.operating_system || null;
  const readinessDimensions = Array.isArray(readiness?.dimensions) ? readiness.dimensions : [];
  const readinessPriorities = Array.isArray(readiness?.top_priorities) ? readiness.top_priorities.slice(0, 5) : [];
  const readinessBlockerItems = Array.isArray(readiness?.blocker_items) ? readiness.blocker_items.slice(0, 4) : [];
  const readinessPrimaryAction = readinessOperating?.primary_action || readiness?.primary_action || primaryAction || null;
  const readinessSecondaryActions = (Array.isArray(readinessOperating?.secondary_actions)
    ? readinessOperating.secondary_actions
    : (Array.isArray(readiness?.secondary_actions) ? readiness.secondary_actions : []))
    .slice(0, 3);
  const readinessTopBlocker = readinessOperating?.top_blocker || readinessBlockerItems[0] || null;
  const readinessFollowSurface = readinessOperating?.follow_surface || null;
  const readinessNextAfterPrimary = readinessOperating?.next_after_primary || readinessSecondaryActions[0] || null;
  const readinessPriorityItems = readinessPriorities
    .filter((item) => item.title !== readinessTopBlocker?.title)
    .filter((item) => item.href !== readinessPrimaryAction?.href)
    .slice(0, 3);
  const followSurfaceTitle = readinessFollowSurface?.key === "frontdoor"
    ? "In den Quick Check"
    : readinessFollowSurface?.label || "Vorbereitung";
  const followSurfaceAnchorLabel = readinessFollowSurface?.key === "frontdoor"
    ? "Jetzt starten"
    : readinessFollowSurface?.key === "documents"
      ? "Jetzt in die Unterlagen"
      : readinessFollowSurface?.key === "b1"
        ? "Jetzt in den Sprachbereich"
        : readinessFollowSurface?.key === "cv"
          ? "Jetzt zum CV"
          : readinessFollowSurface?.key === "messages"
            ? "Jetzt klaeren"
            : readinessFollowSurface?.key === "matches"
              ? "Jetzt zu den Chancen"
              : "Jetzt oeffnen";
  const featuredOpportunity = opportunities[0] || null;
  const secondaryOpportunities = opportunities.slice(1, 4);
  const operatingStateKey = readinessOperating?.current_state_key || "";
  const matchingReady = (
    operatingStateKey === "ready_for_matching"
    || readinessPrimaryAction?.key === "matches"
    || readinessFollowSurface?.key === "matches"
  ) && readiness?.overall_state !== "blocked" && readiness?.overall_state !== "missing";
  const showCoachBriefing = operatingStateKey !== "frontdoor_missing";
  const showOpportunityStage = Boolean(featuredOpportunity) && matchingReady;
  const dailyBriefing = useMemo(
    () => buildApplicantDailyBriefing({ snapshot, messageWorkspace }),
    [messageWorkspace, snapshot]
  );
  const dashboardCoachSurface = useMemo(
    () => buildApplicantDashboardCoachSurface({ snapshot, briefing: dailyBriefing }),
    [dailyBriefing, snapshot]
  );

  useEffect(() => {
    registerCoachSurface(dashboardCoachSurface);
  }, [dashboardCoachSurface, registerCoachSurface]);

  const searchItems = useMemo(() => ([
    ...(readinessPrimaryAction ? [{ href: readinessPrimaryAction.href, label: readinessPrimaryAction.label, description: readinessPriorities[0]?.detail || readiness?.summary || readinessPrimaryAction.detail || readinessPrimaryAction.reason }] : []),
    { href: "/dashboard/applicant/erfolge", label: "Erfolge", description: "Deine Medaillen und dein nächster Erfolg" },
    ...messageHighlights.map((entry) => ({ href: entry.href, label: entry.title, description: entry.text })),
    ...readinessSecondaryActions.map((item) => ({ href: item.href, label: item.label, description: item.detail || readiness?.summary || "" })),
    ...secondaryActions.map((item) => ({ href: item.href, label: item.label, description: item.reason })),
    ...opportunities.map((item) => ({ href: item.href, label: item.title, description: `${item.employer_name} · ${item.region_label}` })),
  ]), [messageHighlights, opportunities, readiness, readinessPrimaryAction, readinessPriorities, readinessSecondaryActions, secondaryActions]);

  const shellNotifications = [
    ...messageHighlights.map((item) => ({
      key: `message-${item.key}`,
      title: item.title,
      text: item.text,
      href: item.href,
    })),
    ...notifications.map((item) => ({
      key: item.key,
      title: item.title,
      text: item.text,
      href: item.href,
    })),
  ].slice(0, 8);

  const go = (href) => navigate(href);

  return (
    <ApplicantShell
      notifications={shellNotifications}
      searchItems={searchItems}
    >
      <div className="applicant-dashboard applicant-dashboard--refined applicant-dashboard--guided">
        {snapshotError ? <div className="dashboard-inline-alert dashboard-inline-alert-danger">{snapshotError}</div> : null}
        {readiness ? (
          <section className="applicant-module-card applicant-readiness-stage applicant-operating-system-stage">
            <div className="applicant-module-card__head applicant-module-card__head--guided">
              <div>
                <span className="applicant-section-kicker">Applicant Operating System</span>
                <h3>Dein naechster Applicant-Schritt ist jetzt klar</h3>
                <p>Das System zieht genau eine Hauptbewegung nach vorn und macht sichtbar, was dich haelt und wohin es als Naechstes geht.</p>
              </div>
              <div className="applicant-readiness-stage__chips">
                <span className={`applicant-readiness-pill is-${readiness.overall_state || "in_progress"}`}>{readiness.overall_state_label || formatReadinessStateCopy(readiness.overall_state)}</span>
                <span className="applicant-readiness-pill is-muted">{readiness.profession_path_label || "Berufspfad"}</span>
              </div>
            </div>

            <article className="applicant-operating-stage">
              <div className="applicant-operating-stage__action">
                <span className="applicant-section-kicker">Jetzt tun</span>
                <strong>{readinessPrimaryAction?.label || "Naechste Hauptaktion"}</strong>
                <p>{readinessOperating?.primary_action_reason || readiness.frontdoor_result_summary || "Das ist jetzt der eine Schritt, der deinen Weg am direktesten bewegt."}</p>
                {readinessPrimaryAction?.href ? (
                  <div className="applicant-stage-actions applicant-stage-actions--operating">
                    <Button className="applicant-cta-button applicant-cta-button-primary-hero" onClick={() => go(readinessPrimaryAction.href)}>
                      {readinessPrimaryAction.label || "Jetzt weitermachen"}
                    </Button>
                  </div>
                ) : null}
              </div>

              {readinessTopBlocker ? (
                <div className="applicant-operating-stage__line applicant-operating-stage__line--blocker">
                  <span className="applicant-section-kicker">Groesster Blocker</span>
                  <strong>{readinessTopBlocker.title}</strong>
                  <p>{readinessTopBlocker.detail}</p>
                  <div className="applicant-operating-stage__meta">
                    <span className={`applicant-readiness-pill is-${readinessTopBlocker.kind === "path_review" ? "blocked" : (readinessTopBlocker.kind === "open_task" ? "missing" : "blocked")}`}>
                      {readinessTopBlocker.kind === "path_review" ? "Pfadwechsel / Review" : readinessTopBlocker.kind === "open_task" ? "Offene Aufgabe" : readinessTopBlocker.kind === "hint" ? "Hinweis" : "Echter Blocker"}
                    </span>
                  </div>
                </div>
              ) : null}

              <div className="applicant-operating-stage__line applicant-operating-stage__line--state">
                <span className="applicant-section-kicker">Aktueller Stand</span>
                <strong>{readinessOperating?.current_state_label || readiness.path_label || "Dein Vorbereitungspfad"}</strong>
                <p>{readinessOperating?.current_state_summary || readiness.summary || "Deine Vorbereitung fuehrt dich jetzt ueber eine klare Hauptbewegung statt ueber lose Statuskarten."}</p>
              </div>

              <div className="applicant-operating-stage__line applicant-operating-stage__line--follow">
                <span className="applicant-section-kicker">Weiter in</span>
                <strong>{followSurfaceTitle}</strong>
                <p>{readinessFollowSurface?.summary || readiness.frontdoor_reasoning?.[0] || readiness.frontdoor_result_summary || "Die Frontdoor hat deinen Weg eingeordnet. Jetzt fuehrt dich die Vorbereitung weiter."}</p>
                <div className="applicant-operating-stage__meta applicant-operating-stage__meta--follow">
                  {readinessFollowSurface?.href ? (
                    <button type="button" className="applicant-inline-link" onClick={() => go(readinessFollowSurface.href)}>
                      <span>{followSurfaceAnchorLabel}</span>
                      <ChevronRight size={15} />
                    </button>
                  ) : null}
                  <small>{readinessNextAfterPrimary?.label ? `Danach: ${readinessNextAfterPrimary.label}.` : "Danach zieht das System den naechsten passenden Bereich nach vorn."}</small>
                </div>
              </div>

              {readinessSecondaryActions.length ? (
                <div className="applicant-operating-stage__secondary">
                  {readinessSecondaryActions.map((item) => (
                    <button key={`${item.key}-${item.href}`} type="button" className="applicant-readiness-route-card applicant-readiness-route-card--secondary" onClick={() => go(item.href)}>
                      <strong>{item.label}</strong>
                      <span>{item.detail || "Direkt in den passenden Bereich springen."}</span>
                    </button>
                  ))}
                </div>
              ) : null}

              {readinessPriorityItems.length ? (
                <div className="applicant-operating-stage__priorities">
                  <div className="applicant-readiness-panel__head">
                    <span className="applicant-section-kicker">Danach wichtig</span>
                    <strong>Die naechsten Prioritaeten</strong>
                  </div>
                  <div className="applicant-action-list applicant-action-list--secondary applicant-action-list--compact">
                    {readinessPriorityItems.map((item) => (
                      <button key={item.key} type="button" className="applicant-action-list__item" onClick={() => go(item.href)}>
                        <div>
                          <strong>{item.title}</strong>
                          <span>{item.detail}</span>
                        </div>
                        <ArrowRight size={15} />
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </article>

            <div className="applicant-readiness-dimension-grid">
              {readinessDimensions.map((dimension) => (
                <article key={dimension.key} className={`applicant-readiness-dimension is-${dimension.state}`}>
                  <div className="applicant-readiness-dimension__head">
                    <div>
                      <span className="applicant-section-kicker">{dimension.label}</span>
                      <strong>{dimension.state_label || formatReadinessStateCopy(dimension.state)}</strong>
                    </div>
                    <span className={`applicant-readiness-pill is-${dimension.state}`}>{dimension.state_label || formatReadinessStateCopy(dimension.state)}</span>
                  </div>
                  <p>{dimension.summary}</p>
                  <div className="applicant-readiness-dimension__meta">
                    {dimension.current_label ? <span><strong>Aktuell:</strong> {dimension.current_label}</span> : null}
                    {dimension.target_label ? <span><strong>Naechstes Ziel:</strong> {dimension.target_label}</span> : null}
                    {dimension.detail ? <small>{dimension.detail}</small> : null}
                  </div>
                  <div className="applicant-readiness-dimension__actions">
                    <button type="button" className="applicant-inline-link" onClick={() => go(dimension.next_step_href)}>
                      <span>{dimension.next_step_label || "Bereich oeffnen"}</span>
                      <ChevronRight size={15} />
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </section>
        ) : null}
        {showCoachBriefing ? (
          <ApplicantDailyBriefing
            briefing={dailyBriefing}
            loading={snapshotLoading || (!messageWorkspace && !snapshotError)}
            error={snapshotError}
            onNavigate={go}
            applicantFirstName={snapshot?.profile?.first_name || ""}
            applicantDisplayName={snapshot?.profile?.display_name || ""}
            forceCompact
          />
        ) : null}

        {showOpportunityStage ? (
          <section className="applicant-module-card applicant-chance-stage applicant-chance-stage--secondary">
            <div className="applicant-module-card__head applicant-module-card__head--guided">
              <div>
                <span className="applicant-section-kicker">Marktoption</span>
                <h3>Diese Chance ist jetzt wirklich relevant</h3>
                <p>Deine Basis traegt. Deshalb darf der Markt jetzt als naechste offene Sekundaerebene sichtbar werden.</p>
              </div>
            </div>
            <div className="applicant-opportunity-radar applicant-opportunity-radar--guided">
              <article className="applicant-opportunity-feature applicant-opportunity-feature--guided">
                <div className="applicant-opportunity-card__meta">
                  <span className="applicant-opportunity-card__fit">{featuredOpportunity.fit_band}</span>
                  <small>{formatAbsoluteDate(featuredOpportunity.released_at)}</small>
                </div>
                <strong>{featuredOpportunity.title}</strong>
                <p>{featuredOpportunity.employer_name} · {featuredOpportunity.region_label}</p>
                <div className="applicant-opportunity-guidance-grid">
                  <section className="applicant-opportunity-guidance">
                    <span className="applicant-section-kicker">Warum sie jetzt passt</span>
                    <ul className="applicant-guidance-bullets">
                      {(featuredOpportunity.why_relevant || []).slice(0, 3).map((reason) => (
                        <li key={reason}>{reason}</li>
                      ))}
                    </ul>
                  </section>
                  <section className="applicant-opportunity-guidance">
                    <span className="applicant-section-kicker">Was du noch pruefen solltest</span>
                    {featuredOpportunity.missing_items?.length ? (
                      <ul className="applicant-guidance-bullets applicant-guidance-bullets--muted">
                        {featuredOpportunity.missing_items.slice(0, 3).map((item) => (
                          <li key={item.label}>{item.reason || item.label}</li>
                        ))}
                      </ul>
                    ) : (
                      <p>{featuredOpportunity.readiness_label || "Deine Basis wirkt fuer diesen Schritt bereits tragfaehig."}</p>
                    )}
                  </section>
                </div>
                <div className="applicant-opportunity-actions">
                  <span className="applicant-section-kicker">Jetzt im Markt</span>
                  <strong>{featuredOpportunity.next_step_label || "Chance als naechsten Marktweg pruefen"}</strong>
                  <p>Diese Chance ist erst jetzt offen sichtbar, weil sie zu deinem aktuellen Applicant-Zustand wirklich passt.</p>
                  <button type="button" className="applicant-inline-link" onClick={() => go(featuredOpportunity.next_step_href || featuredOpportunity.href)}>
                    <span>{featuredOpportunity.next_step_label || "Chance jetzt pruefen"}</span>
                    <ChevronRight size={15} />
                  </button>
                  {featuredOpportunity.next_step_href && featuredOpportunity.next_step_href !== featuredOpportunity.href ? (
                    <button type="button" className="applicant-inline-link" onClick={() => go(featuredOpportunity.href)}>
                      <span>Chance im Detail ansehen</span>
                      <ChevronRight size={15} />
                    </button>
                  ) : null}
                </div>
              </article>
              <div className="applicant-opportunity-stack">
                {secondaryOpportunities.length ? secondaryOpportunities.map((item) => (
                  <article key={item.job_id} className="applicant-opportunity-card">
                    <div className="applicant-opportunity-card__meta">
                      <span className="applicant-opportunity-card__fit">{item.fit_band}</span>
                      <small>{formatAbsoluteDate(item.released_at)}</small>
                    </div>
                    <strong>{item.title}</strong>
                    <p>{item.employer_name} · {item.region_label}</p>
                    <div className="applicant-opportunity-card__reason">{item.why_relevant?.[0] || item.readiness_label || "Passt aktuell zu deinem Profil."}</div>
                    <div className="applicant-opportunity-card__foot">
                      <span>{item.next_step_label || "Chance ansehen"}</span>
                      <button type="button" className="applicant-inline-link" onClick={() => go(item.next_step_href || item.href)}>
                        <span>Jetzt pruefen</span>
                        <ChevronRight size={15} />
                      </button>
                    </div>
                  </article>
                )) : (
                  <div className="applicant-empty-card">Gerade steht diese eine Chance sichtbar im Vordergrund.</div>
                )}
              </div>
            </div>
          </section>
        ) : null}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent fullScreen className="dashboard-dialog">
            <DialogHeader className="dashboard-dialog__header">
              <DialogTitle className="dashboard-dialog__title">Sprachtest B1 Core</DialogTitle>
              <DialogDescription className="dashboard-dialog__description">Deterministische Kernmodule, KI-gestütztes Schreiben und Audioaufnahme fürs Sprechen.</DialogDescription>
            </DialogHeader>
            <div className="dashboard-dialog__content">
              <div className="dashboard-dialog__main">
                <section className="dashboard-dialog__section"><h3>Lesen</h3>{renderChoiceModule(currentModules.reading?.items, "reading", draftState?.reading, (moduleKey, itemId, value) => { setDraftState((current) => ({ ...current, [moduleKey]: { ...(current?.[moduleKey] || {}), [itemId]: Number(value) } })); setDraftDirty(true); })}</section>
              <section className="dashboard-dialog__section"><h3>Hören</h3>{renderChoiceModule(currentModules.listening?.items, "listening", draftState?.listening, (moduleKey, itemId, value) => { setDraftState((current) => ({ ...current, [moduleKey]: { ...(current?.[moduleKey] || {}), [itemId]: Number(value) } })); setDraftDirty(true); })}</section>
              <section className="dashboard-dialog__section"><h3>Sprachbausteine</h3>{renderChoiceModule(currentModules.language_elements?.items, "language_elements", draftState?.language_elements, (moduleKey, itemId, value) => { setDraftState((current) => ({ ...current, [moduleKey]: { ...(current?.[moduleKey] || {}), [itemId]: Number(value) } })); setDraftDirty(true); })}</section>
              <section className="dashboard-dialog__section"><h3>Schreiben</h3><div className="dashboard-question-card"><p className="dashboard-question-card__copy">{currentModules.writing?.instructions}</p><textarea className="dashboard-textarea" placeholder="Schreibe deine Antwort hier." value={draftState?.writing_answer || ""} onChange={(event) => { setDraftState((current) => ({ ...current, writing_answer: event.target.value })); setDraftDirty(true); }} /></div></section>
              <section className="dashboard-dialog__section"><h3>Sprechen</h3><div className="dashboard-question-card"><p className="dashboard-question-card__meta">Audioaufgabe</p><ul className="dashboard-bullet-list">{(currentModules.speaking?.prompts || []).map((prompt) => <li key={prompt}>{prompt}</li>)}</ul></div><B1SpeakingAudioRecorder attemptId={activeAttempt?.id} currentAudio={activeAttempt?.speaking_audio || draftState?.speaking_audio_meta} speakingStatus={activeAttempt?.speaking_status || draftState?.speaking_status} invalidReason={activeAttempt?.speaking_invalid_reason || draftState?.speaking_invalid_reason} prompts={currentModules.speaking?.prompts || []} disabled={actionBusy || !activeAttempt} onUpload={uploadSpeakingAudio} /></section>
            </div>
            <aside className="dashboard-dialog__side">
              <section className="dashboard-dialog__section"><h3>Versuchssteuerung</h3><div className="dashboard-surface-grid"><div className="dashboard-surface"><span className="dashboard-summary-label">Versuchstyp</span><strong>{formatAttemptType(activeAttempt?.test_type)}</strong></div><div className="dashboard-surface"><span className="dashboard-summary-label">Core-Version</span><strong>{activeAttempt?.core_version || "b1_core_v1"}</strong></div><div className="dashboard-surface"><span className="dashboard-summary-label">Entwurfsstand</span><strong>{draftDirty ? "Ungespeichert" : "Synchron"}</strong></div></div><div className="dashboard-inline-alert">Lesen, Hören und Sprachbausteine werden deterministisch bewertet. Schreiben und Sprechen bleiben KI-gestützt.</div><div className="dashboard-dialog__actions"><Button variant="outline" className="dashboard-action-button dashboard-action-button-secondary" onClick={saveDraft} disabled={actionBusy || !activeAttempt}>Entwurf speichern</Button><Button className="dashboard-action-button" onClick={submitAttempt} disabled={actionBusy || !activeAttempt}>B1-Core absenden</Button></div></section>
              <section className="dashboard-dialog__section"><h3>Historie</h3><Accordion type="single" collapsible className="dashboard-accordion"><AccordionItem value="attempts" className="dashboard-accordion__item"><AccordionTrigger className="dashboard-accordion__trigger">Letzte Versuche</AccordionTrigger><AccordionContent className="dashboard-accordion__content"><div className="dashboard-history-list">{attempts.length ? attempts.slice(0, 6).map((attempt) => <div key={attempt.id} className="dashboard-history-item"><div><strong>Versuch #{attempt.id}</strong><p>{formatAttemptType(attempt.test_type)} · {formatReviewStatus(attempt.review_status)}</p></div><div className="dashboard-history-meta"><span>{attempt.estimated_cefr_band || "Noch offen"}</span><small>{formatDate(attempt.completed_at || attempt.created_at)}</small></div></div>) : <div className="dashboard-empty-tile">Noch keine gespeicherten Versuche vorhanden.</div>}</div></AccordionContent></AccordionItem></Accordion></section>
            </aside>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={sessionExpiredOpen} onOpenChange={() => {}}>
        <DialogContent className="max-w-[460px]">
          <DialogHeader>
            <DialogTitle>Sitzung abgelaufen</DialogTitle>
            <DialogDescription>{sessionExpiredMessage}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap justify-end gap-3">
            <Button
              variant="outline"
              className="applicant-cta-button applicant-cta-button-secondary"
              onClick={() => navigate("/")}
            >
              Startseite
            </Button>
            <Button
              className="applicant-cta-button"
              onClick={() => navigate("/login", { replace: true })}
            >
              Erneut anmelden
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </ApplicantShell>
  );
}
